import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ArrowRight, CheckCircle2, ExternalLink, type LucideIcon } from "lucide-react";

type SectionShellProps = {
  id?: string;
  eyebrow?: string;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
};

export const SectionShell = ({
  id,
  eyebrow,
  title,
  description,
  children,
  className,
}: SectionShellProps) => (
  <section id={id} className={cn("py-14 md:py-20", className)}>
    <div className="container px-4">
      <div className="mb-10 max-w-3xl space-y-4">
        {eyebrow ? (
          <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
            {eyebrow}
          </div>
        ) : null}
        <h2 className="text-3xl font-bold leading-tight md:text-4xl">{title}</h2>
        {description ? (
          <p className="text-base leading-relaxed text-muted-foreground md:text-lg">
            {description}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  </section>
);

type IconCardItem = {
  icon: LucideIcon;
  title: string;
  description: string;
};

type IconCardGridProps = {
  items: IconCardItem[];
  columns?: "two" | "three" | "four";
};

export const IconCardGrid = ({ items, columns = "three" }: IconCardGridProps) => {
  const gridClass =
    columns === "two"
      ? "md:grid-cols-2"
      : columns === "four"
      ? "md:grid-cols-2 xl:grid-cols-4"
      : "md:grid-cols-3";

  return (
    <div className={cn("grid gap-5", gridClass)}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <Card key={item.title} className="scan-line border-border/80 bg-card/80 shadow-[var(--shadow-soft)]">
            <CardHeader className="space-y-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/25 bg-primary/10">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div className="space-y-2">
                <CardTitle className="text-xl">{item.title}</CardTitle>
                <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                  {item.description}
                </CardDescription>
              </div>
            </CardHeader>
          </Card>
        );
      })}
    </div>
  );
};

type StepItem = {
  step: string;
  title: string;
  description: string;
};

type StepListProps = {
  items: StepItem[];
};

export const StepList = ({ items }: StepListProps) => (
  <div className="grid gap-5 md:grid-cols-3">
    {items.map((item) => (
      <Card key={item.step} className="border-border/80 bg-card/80">
        <CardHeader className="space-y-4">
          <div className="inline-flex w-fit items-center rounded-full border border-accent/30 bg-accent/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.22em] text-accent">
            {item.step}
          </div>
          <CardTitle className="text-xl">{item.title}</CardTitle>
          <CardDescription className="leading-relaxed text-muted-foreground">
            {item.description}
          </CardDescription>
        </CardHeader>
      </Card>
    ))}
  </div>
);

type JumpLinkItem = {
  href: string;
  label: string;
};

type JumpLinksStripProps = {
  title: string;
  items: JumpLinkItem[];
};

export const JumpLinksStrip = ({ title, items }: JumpLinksStripProps) => (
  <div className="rounded-2xl border border-border/80 bg-card/80 p-5">
    <div className="space-y-4">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-primary">{title}</p>
      <div className="flex flex-wrap gap-3">
        {items.map((item) => (
          <a
            key={item.href}
            href={item.href}
            className="rounded-full border border-border/80 bg-background/70 px-4 py-2 text-sm text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
          >
            {item.label}
          </a>
        ))}
      </div>
    </div>
  </div>
);

type PricingCardItem = {
  name: string;
  price: string;
  summary: string;
  features: string[];
  badge?: string;
};

type PricingCardsProps = {
  items: PricingCardItem[];
};

export const PricingCards = ({ items }: PricingCardsProps) => (
  <div className="grid gap-5 lg:grid-cols-2">
    {items.map((item) => (
      <Card
        key={item.name}
        className={cn(
          "border-border/80 bg-card/80",
          item.badge ? "ring-1 ring-primary/25" : "",
        )}
      >
        <CardHeader className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-2xl">{item.name}</CardTitle>
              <CardDescription className="mt-2 max-w-md leading-relaxed text-muted-foreground">
                {item.summary}
              </CardDescription>
            </div>
            {item.badge ? (
              <Badge className="border border-primary/25 bg-primary/10 text-primary hover:bg-primary/10">
                {item.badge}
              </Badge>
            ) : null}
          </div>
          <div className="flex items-end gap-2">
            <span className="font-heading text-4xl font-bold text-foreground">{item.price}</span>
            <span className="pb-1 text-sm text-muted-foreground">per domain / month</span>
          </div>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {item.features.map((feature) => (
              <li key={feature} className="flex items-start gap-3 text-sm text-muted-foreground">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[#22C373]" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    ))}
  </div>
);

type TrialStripProps = {
  title: string;
  body: string;
  note: string;
};

export const TrialExplainerStrip = ({ title, body, note }: TrialStripProps) => (
  <div className="rounded-2xl border border-primary/20 bg-[linear-gradient(135deg,rgba(25,212,230,0.08),rgba(20,24,31,0.95))] p-6 shadow-[var(--shadow-elevated)]">
    <div className="grid gap-5 lg:grid-cols-[1.1fr,0.9fr] lg:items-start">
      <div className="space-y-3">
        <div className="inline-flex rounded-full border border-primary/20 bg-background/40 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
          current campaign path
        </div>
        <h3 className="text-2xl font-bold">{title}</h3>
        <p className="max-w-2xl leading-relaxed text-muted-foreground">{body}</p>
      </div>
      <div className="rounded-xl border border-border/70 bg-background/60 p-4">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent">Important note</p>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{note}</p>
      </div>
    </div>
  </div>
);

type CtaSectionProps = {
  eyebrow: string;
  title: string;
  description: string;
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  className?: string;
};

export const CtaSection = ({
  eyebrow,
  title,
  description,
  primaryLabel,
  primaryHref,
  secondaryLabel,
  secondaryHref,
  onPrimaryClick,
  onSecondaryClick,
  className,
}: CtaSectionProps) => (
  <div className={cn("rounded-2xl border border-border/80 bg-card/80 p-6 md:p-8", className)}>
    <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
      <div className="max-w-2xl space-y-3">
        <div className="inline-flex rounded-full border border-accent/20 bg-accent/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-accent">
          {eyebrow}
        </div>
        <h3 className="text-2xl font-bold md:text-3xl">{title}</h3>
        <p className="text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          asChild
          size="lg"
          className="bg-accent text-accent-foreground hover:bg-accent/90"
        >
          <Link to={primaryHref} onClick={onPrimaryClick}>
            {primaryLabel}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
        {secondaryLabel && secondaryHref ? (
          secondaryHref.startsWith("/") ? (
            <Button asChild size="lg" variant="outline">
              <Link to={secondaryHref} onClick={onSecondaryClick}>
                {secondaryLabel}
              </Link>
            </Button>
          ) : (
            <Button asChild size="lg" variant="outline">
              <a href={secondaryHref} onClick={onSecondaryClick}>
                {secondaryLabel}
              </a>
            </Button>
          )
        ) : null}
      </div>
    </div>
  </div>
);

type FaqItem = {
  question: string;
  answer: ReactNode;
};

type CampaignFaqProps = {
  items: FaqItem[];
};

export const CampaignFaq = ({ items }: CampaignFaqProps) => (
  <Accordion type="single" collapsible className="rounded-2xl border border-border/80 bg-card/80 px-6">
    {items.map((item, index) => (
      <AccordionItem key={item.question} value={`faq-${index}`} className="border-border/60">
        <AccordionTrigger className="text-left text-base hover:no-underline">
          {item.question}
        </AccordionTrigger>
        <AccordionContent className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
          {item.answer}
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

type TerminalNoteProps = {
  label: string;
  title: string;
  body: string;
};

export const TerminalNote = ({ label, title, body }: TerminalNoteProps) => (
  <div className="rounded-2xl border border-border/80 bg-[#10151c] p-5 shadow-[var(--shadow-soft)]">
    <div className="flex items-center justify-between gap-3 border-b border-border/70 pb-3">
      <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-primary">
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#E23636]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#F9A91F]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#22C373]" />
      </div>
    </div>
    <div className="space-y-3 pt-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  </div>
);

type ExternalSupportLinkProps = {
  href: string;
  label: string;
  onClick?: () => void;
};

export const ExternalSupportLink = ({ href, label, onClick }: ExternalSupportLinkProps) => (
  <a
    href={href}
    onClick={onClick}
    className="inline-flex items-center gap-2 text-sm font-medium text-primary transition hover:text-primary/80"
  >
    {label}
    <ExternalLink className="h-3.5 w-3.5" />
  </a>
);
